import React, { useCallback, useEffect } from 'react';
import moment from 'moment';
import {
  Button,
  Card,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import fs from 'file-saver';
import { CellValue, Workbook } from 'exceljs';
import Scrollbar from '../../components/Scrollbar';
import Iconify from '../../components/Iconify';
import Page from '../../components/Page';
import { BookingService } from '../../Services/booking.service';
import axiosClient from '../../api/AxiosConfig';
import { useLoadingContext } from '../../context/loadingContext';

const BookingPage = () => {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [total, setTotal] = React.useState(0);
  const [store, setStore] = React.useState([]);
  const [filter, setFilter] = React.useState({
    fromDate: new Date(),
    toDate: new Date(),
  });
  const [local, setLocal] = React.useState('');
  const [bookings, setBookings] = React.useState([]);
  const { handleStartLoading, handleStopLoading } = useLoadingContext();

  useEffect(() => {
    const getStore = async () => {
      handleStartLoading();
      const data = await axiosClient.get('/gateway/store');
      setStore(data.data);
    };

    getStore().finally(handleStopLoading);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilter((prevState) => ({
      ...prevState,
      [name]: new Date(value),
    }));
  };

  const handleSearchClick = async () => {
    try {
      handleStartLoading();
      const bookingsData = await BookingService.GetBookings({
        page: 1,
        pageSize,
        address: local,
        ...filter,
      });
      setPage(0);
      setBookings(bookingsData.data);
      setTotal(bookingsData.pagination.count);
    } catch (error) {
      console.log(error);
    } finally {
      handleStopLoading();
    }
  };

  const handlePageChange = async (_, newPage) => {
    try {
      handleStartLoading();
      setPage(newPage);
      const bookingsData = await BookingService.GetBookings({
        page: newPage + 1,
        pageSize,
        address: local,
        ...filter,
      });
      setBookings(bookingsData.data);
      setTotal(bookingsData.pagination.count);
    } catch (error) {
      console.log(error);
    } finally {
      handleStopLoading();
    }
  };

  const handleRowsPerPageChange = async (event) => {
    try {
      handleStartLoading();
      setPageSize(event.target.value);
      setPage(0);
      const bookingsData = await BookingService.GetBookings({
        page: 1,
        pageSize: event.target.value,
        address: local,
        ...filter,
      });
      setBookings(bookingsData.data);
      setTotal(bookingsData.pagination.count);
    } catch (error) {
      console.log(error);
    } finally {
      handleStopLoading();
    }
  };

  const getStoreName = useCallback(
    (idx) => {
      const index = store.findIndex((x) => x.Id.toString() === idx.toString());
      return index !== -1 ? store[index].Pos_Name : '';
    },
    [store]
  );

  const handleExport = () => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('raw_data');
    worksheet.columns = [
      {
        key: 'index',
        header: 'Số thứ tự',
        width: 16,
        style: { font: { size: 13, bold: true } },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
      },
      { key: 'bookingStatus', header: 'Trạng thái', width: 16 },
      { key: 'fullName', header: 'Họ và tên', width: 24 },
      { key: 'phoneNumber', header: 'Số điện thoại', width: 13 },
      {
        key: 'amount',
        header: 'Số khách',
        width: 11,
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
      },
      { key: 'address', header: 'Chi nhánh', width: 28 },
      { key: 'notes', header: 'Ghi chú', width: 16 },
      { key: 'date', header: 'Ngày đến', width: 13 },
      {
        key: 'time',
        header: 'Thời gian đến',
        width: 13,
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
      },
      { key: 'createdAt', header: 'Ngày đặt', width: 16 },
    ];

    bookings.forEach((item, index) => {
      worksheet.addRow([
        index + 1,
        convertStatus(item.bookingStatus),
        item.fullName,
        item.phoneNumber,
        item.amount,
        getStoreName(item.address),
        item.notes,
        moment(item.date).format('DD/MM/yyyy'),
        item.time,
        moment(item.createdAt).format('DD/MM/yyyy HH:mm'),
      ]);
    });
    const valueTotal = (status) => {
      const sum = bookings.reduce((total, item) => {
        if (item.bookingStatus === status) {
          total += 1;
        }
        return total;
      }, 0);
      return sum;
    };
    worksheet.addRow(['Tổng hoàn thành: ', valueTotal('Done')]);
    worksheet.addRow(['Tổng không nhận: ', valueTotal('Cancel')]);
    worksheet.addRow(['Tổng bị huỷ: ', valueTotal('Stop')]);
    worksheet.addRow(['Tổng chờ xác nhận: ', valueTotal('Pending')]);
    worksheet.addRow(['Tổng đang nhận: ', valueTotal('Accept')]);

    worksheet.getColumn(5).alignment = {
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    };
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      fs.saveAs(blob, 'danh_sach_dat_ban.xlsx');
    });
  };

  const convertStatus = useCallback((status) => {
    switch (status) {
      case 'Pending':
        return 'Chờ xác nhận';
      case 'Accept':
        return 'Nhà hàng nhận đơn';
      case 'Cancel':
        return 'Không nhận đơn';
      case 'Done':
        return 'Hoàn thành';
      case 'Stop':
        return 'Huỷ đơn';
      default:
        return '';
    }
  }, []);

  return (
    <Page title="Booking">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Danh sách đặt bàn
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
            <FormControl sx={{ width: '268px' }} size="small">
              <InputLabel id="address">Địa điểm</InputLabel>
              <Select onChange={(e) => setLocal(e.target.value)} value={local} name="address" label="Địa điểm">
                <MenuItem value="">
                  <em>Tất cả</em>
                </MenuItem>
                {store?.map((item) => (
                  <MenuItem key={item.Id} value={item.Id}>
                    {item.Pos_Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              name="fromDate"
              value={filter.fromDate.toLocaleDateString('en-CA')}
              onChange={handleChange}
              type={'date'}
              size="small"
            />
            <Iconify icon={'akar-icons:arrow-right'} />
            <TextField
              InputProps={{ inputProps: { min: filter.fromDate.toLocaleDateString('en-CA') } }}
              type={'date'}
              name="toDate"
              value={filter.toDate.toLocaleDateString('en-CA')}
              onChange={handleChange}
              size="small"
            />
            <Button variant="contained" onClick={handleSearchClick} startIcon={<Iconify icon="bx:search-alt-2" />}>
              Tìm
            </Button>
            {bookings.length > 0 && (
              <Button
                variant="contained"
                color="info"
                onClick={handleExport}
                startIcon={<Iconify icon="tabler:file-export" />}
              >
                Xuất báo cáo
              </Button>
            )}
          </Stack>
        </Stack>
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 900 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={'60px'}>STT</TableCell>
                    <TableCell>Khách hàng</TableCell>
                    <TableCell>Số điện thoại</TableCell>
                    <TableCell>Nhà hàng</TableCell>
                    <TableCell>Ngày đến</TableCell>
                    <TableCell>Thời gian</TableCell>
                    <TableCell>Ghi chú</TableCell>
                    <TableCell>Trạng thái</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookings.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center' }}>
                        Không có dữ liệu
                      </TableCell>
                    </TableRow>
                  )}
                  {bookings.length > 0 &&
                    bookings.map((booking, index) => (
                      <TableRow key={booking._id} hover>
                        <TableCell width={60} align="center">
                          {index + 1}
                        </TableCell>
                        <TableCell>{booking.fullName}</TableCell>
                        <TableCell>{booking.phoneNumber}</TableCell>
                        <TableCell>{getStoreName(booking.address)}</TableCell>
                        <TableCell>{moment(booking.date).format('DD/MM/yyyy')}</TableCell>
                        <TableCell>{booking.time}</TableCell>
                        <TableCell>{booking.notes}</TableCell>
                        <TableCell sx={{ color: 'error' }}>
                          {booking.bookingStatus === 'Cancel' && (
                            <Typography color="error" variant="overline">
                              Không nhận đơn
                            </Typography>
                          )}
                          {booking.bookingStatus === 'Pending' && <Typography color="orange">Chờ xác nhận</Typography>}
                          {booking.bookingStatus === 'Accept' && (
                            <Typography color="blue">Nhà hàng nhận đơn</Typography>
                          )}
                          {booking.bookingStatus === 'Done' && <Typography color="green">Hoàn thành</Typography>}
                          {booking.bookingStatus === 'Stop' && <Typography color="red">Huỷ đơn</Typography>}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          {bookings && bookings.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 100, 500, 1000]}
              component="div"
              count={total}
              rowsPerPage={pageSize}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          )}
        </Card>
      </Container>
    </Page>
  );
};

export default BookingPage;
